'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { Loader2, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { createSupabaseBrowserClient } from '@/lib/supabase/client';

export function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectTo = searchParams.get('redirect') ?? '/dashboard';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const supabase = createSupabaseBrowserClient();
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    setLoading(false);
    if (error) {
      toast.error(
        error.message === 'Invalid login credentials'
          ? 'Email o contraseña incorrectos.'
          : error.message,
      );
      return;
    }
    toast.success('Sesión iniciada correctamente.');
    router.push(redirectTo);
    router.refresh();
  };

  return (
    <Card className="border-gold-500/20">
      <CardHeader className="space-y-2 text-center">
        <CardTitle className="font-display text-3xl">Bienvenido de nuevo</CardTitle>
        <CardDescription>
          Accede para continuar tu progreso en Oro Express Academy.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              required
              placeholder="tu@email.com"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Contraseña</Label>
              <Link
                href="/recuperar"
                className="text-xs text-gold-300 hover:underline"
              >
                ¿La olvidaste?
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              required
              placeholder="••••••••"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={6}
            />
          </div>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            disabled={loading}
            className="w-full"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <LogIn className="h-4 w-4" /> Iniciar sesión
              </>
            )}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-cream/65">
          ¿Aún no tienes cuenta?{' '}
          <Link href="/registro" className="text-gold-300 hover:underline">
            Regístrate gratis
          </Link>
        </p>
        <div className="mt-6 rounded-xl border border-gold-500/15 bg-gold-500/5 p-3 text-center text-xs text-cream/60">
          <strong className="text-gold-300">Demo:</strong>{' '}
          <span className="font-mono">demo@oro-express.es</span> /{' '}
          <span className="font-mono">Demo1234!</span>
        </div>
      </CardContent>
    </Card>
  );
}
